<template>
  <div v-if="!isServiceEmployee && isBuyable" class="vc-add-to-cart">
    <div v-if="showQuantityMetersChoice && productWidthProperty > 0" class="mb-3 flex">
      <VcRadioButton
        v-model="selectedMeasureType"
        :value="activeMeasureType"
        :label="$t('custom.add_to_cart.pieces')"
        class="mr-4"
      />
      <VcRadioButton v-model="selectedMeasureType" value="1" :label="$t('custom.add_to_cart.meters')" />
    </div>
    <div class="relative z-0 flex">
      <VcInput
        v-if="selectedMeasureType === '0'"
        ref="inputElement"
        v-model.number="enteredQuantity"
        type="number"
        :disabled="disabled"
        :max="maxQuantity"
        center
        :min="minQuantity"
        :class="{
          'z-10 border-[color:var(--color-danger)] focus:border-[color:var(--color-danger-hover)]': !!errorMessage,
        }"
        class="w-1/3"
        @input="onInput"
        @keypress="onKeypress"
        @blur="onBlur"
      />

      <VcInput
        v-if="selectedMeasureType === '1'"
        ref="inputElementMeters"
        v-model.number="enteredMeters"
        center
        type="number"
        :disabled="disabled"
        :class="{
          'z-10 border-[color:var(--color-danger)] focus:border-[color:var(--color-danger-hover)]': !!errorMessage,
        }"
        class="w-1/3"
        @input="onMetersInput"
        @blur="onMetersBlur"
      />

      <VcButton
        class="ml-3 w-2/3 rounded"
        :variant="!countInCart ? 'solid' : 'outline'"
        color="secondary"
        :loading="loading"
        :disabled="isDisabled"
        :title="buttonText"
        size="md"
        truncate
        @click="onChange()"
      >
        {{ buttonText }}
      </VcButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/yup";
import { debounce } from "lodash";
import { toRefs } from "@vueuse/core";
import { useField } from "vee-validate";
import { computed, ref, shallowRef, watch, toRef, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useUser } from "@/shared/account";
import { useShortCart } from "@/shared/cart/composables";
import { useNotifications } from "@/shared/notification";
import { useQuantityValidationSchema } from "@/ui-kit/composables";
import type { Product, VariationType } from "@/core/api/graphql/types";
import { useModuleSettings } from "@/core/composables/useModuleSettings";
import {
  LINE_ITEM_QUANTITY_LIMIT,
  API_MANAGEMENT_MODULE,
  ENABLE_LENGTH_CONVERTER_SETTING,
  ENABLE_PACK_SIZE_QUANTITY,
} from "@/core/constants";

type ExtendedVariationType = VariationType & {
  dimensionsWidth?: number;
  herasPacksize?: number | undefined;
};

interface IEmits {
  (event: "update:modelValue", value: number): void;
  (event: "update:cartItemQuantity", quantity: number): void;
  (event: "update:validation", value: { isValid: true } | { isValid: false; errorMessage: string }): void;
}

interface IProps {
  product: Product | ExtendedVariationType;
  name?: string;
  loading?: boolean;
  modelValue?: number;
  disabled?: boolean;
  minQuantity: number;
  maxQuantity?: number;
  packSize?: number;
  countInCart: number;
  availableQuantity?: number;
  isActive?: boolean;
  isAvailable?: boolean;
  isBuyable?: boolean;
  isInStock?: boolean;
  message?: string;
  showEmptyDetails?: boolean;
  error?: boolean;
  hideButton?: boolean;
  readonly?: boolean;
  timeout?: number;
  validateOnMount?: boolean;
  size?: "xs" | "sm" | "md";
}

const emit = defineEmits<IEmits>();

const props = withDefaults(defineProps<IProps>(), {
  validateOnMount: true,
  size: "sm",
});

const isValid = ref(true);

const {
  timeout,
  disabled,
  isInStock,
  minQuantity,
  maxQuantity,
  availableQuantity,
  isActive,
  isAvailable,
  isBuyable,
  packSize,
  countInCart,
} = toRefs(props);

const { getSettingValue } = useModuleSettings(API_MANAGEMENT_MODULE);
const enableFenceLengthConverter = getSettingValue(ENABLE_LENGTH_CONVERTER_SETTING);

const notifications = useNotifications();
const modelValue = toRef(props, "modelValue");
const product = toRef(props, "product");

const pendingQuantity = ref<number | null>(null);

// Innvadis custom check Package qty
const packSizeEnabled = getSettingValue(ENABLE_PACK_SIZE_QUANTITY);

const defaultQuantity = computed<number>(() => product.value?.herasPacksize ?? 1);

const { isServiceEmployee } = useUser();

// Define max qty available to add
const MAX_VALUE = 999999999;

const { cart, addToCart, changeItemQuantity } = useShortCart();
const { t } = useI18n();
const { quantitySchema } = useQuantityValidationSchema({
  availableQuantity,
  minQuantity,
  maxQuantity,
  packSize,
});

const buttonText = computed<string>(() =>
  countInCart.value ? t("common.buttons.update_cart") : t("common.buttons.add_to_cart"),
);
const rules = computed(() => toTypedSchema(quantitySchema.value));
const inputElement = shallowRef<HTMLInputElement>();
const activeMeasureType = ref<string>("0");
// Innovadis custom check
// Helper to snap quantity to nearest valid pack size increment
function snapToPackSize(quantity: number | undefined): number | undefined {
  if (!packSizeEnabled || !product.value?.herasPacksize || product.value.herasPacksize <= 0 || !quantity) {
    return quantity;
  }
  const packSize = product.value.herasPacksize;
  // Always round up to the nearest pack size
  return Math.max(packSize, Math.ceil(quantity / packSize) * packSize);
}

const enteredQuantity = ref(
  !disabled.value
    ? countInCart.value || (packSizeEnabled ? snapToPackSize(defaultQuantity.value) : props.minQuantity)
    : undefined,
);

const isDisabled = computed(
  () =>
    !isValid.value || disabled.value || !isAvailable.value || !isBuyable.value || !isInStock.value || !isActive?.value,
);

const { errorMessage, validate, setValue } = useField("quantity", rules, {
  initialValue: enteredQuantity,
  validateOnMount: true,
});

async function validateFields(): Promise<void> {
  if (isInStock.value && !disabled.value && isAvailable.value && isBuyable.value && isActive?.value) {
    const { valid } = await validate();
    isValid.value = valid;
    if (!valid && errorMessage.value) {
      emit("update:validation", { isValid: false, errorMessage: errorMessage.value });
    } else {
      emit("update:validation", { isValid: true });
    }
  } else {
    isValid.value = true;
    emit("update:validation", { isValid: true });
  }
}

async function handleChange() {
  if (packSizeEnabled) {
    enteredQuantity.value = snapToPackSize(enteredQuantity.value);
  }
  setValue(enteredQuantity.value);
  const newQuantity = Number(enteredQuantity.value);

  if (isNaN(newQuantity) || newQuantity < 1) {
    return;
  }

  await validateFields();

  emit("update:modelValue", newQuantity);
  emit("update:cartItemQuantity", newQuantity);
  pendingQuantity.value = newQuantity;
}

async function onChange() {
  if (enteredQuantity.value && enteredQuantity.value > LINE_ITEM_QUANTITY_LIMIT) {
    enteredQuantity.value = Number(enteredQuantity.value.toString().slice(0, -1));
  }
  await handleChange();
}

/**
 * Ignore non-numeric keys.
 */
function onKeypress(event: KeyboardEvent) {
  if (!/[0-9]/.test(event.key)) {
    event.preventDefault();
  }
}

/**
 * Limit max value.
 */
function onInput() {
  if (!enteredQuantity.value) {
    enteredQuantity.value = undefined;
  } else if (enteredQuantity.value > LINE_ITEM_QUANTITY_LIMIT) {
    enteredQuantity.value = LINE_ITEM_QUANTITY_LIMIT;
  } else {
    // Do NOT snap to pack size here; allow free typing
    enteredMeters.value = enteredQuantity.value! * (productWidthProperty.value / 1000);
    setValue(enteredQuantity.value);
  }
}

function onMetersInput() {
  if (!enteredMeters.value || enteredMeters.value < 0) {
    enteredMeters.value = undefined;
  } else if (enteredMeters.value > MAX_VALUE) {
    enteredMeters.value = MAX_VALUE;
  } else {
    calculateQuantity();
  }
}

function onBlur() {
  if (!enteredQuantity.value || enteredQuantity.value < 1) {
    enteredQuantity.value = countInCart.value || props.minQuantity;
  }
  if (packSizeEnabled) {
    enteredQuantity.value = snapToPackSize(enteredQuantity.value);
  }
}

/**
 * Select input value.
 */

/**
 * INNO custom feature: Add product to the cart based on the width and required customer fence length.
 */

const inputElementMeters = shallowRef<HTMLInputElement>();

const showQuantityMetersChoice = computed<boolean | undefined>(
  () => product.value?.code?.startsWith("C") && !!enableFenceLengthConverter,
);

const productWidthProperty = computed<number>(() => {
  const width = product.value.dimensionsWidth;
  return width && width > 0 ? width : 1;
});

const enteredMeters = ref(
  !disabled.value
    ? productWidthProperty.value > 0
      ? (productWidthProperty.value * (countInCart.value || props.minQuantity)) / 1000
      : undefined
    : undefined,
);

const _selectedMeasureType = ref<string>();
const selectedMeasureType = computed({
  get: () => _selectedMeasureType.value ?? "0",
  set: (value) => (_selectedMeasureType.value = value),
});

function calculateQuantity(): number {
  if (enteredMeters.value && showQuantityMetersChoice.value) {
    let endQty = Math.ceil((enteredMeters.value * 1000) / productWidthProperty.value);
    if (packSizeEnabled) {
      endQty = snapToPackSize(endQty)!;
    }
    enteredQuantity.value = endQty;
    setValue(endQty);
    return endQty;
  } else if (enteredQuantity.value) {
    if (packSizeEnabled) {
      enteredQuantity.value = snapToPackSize(enteredQuantity.value);
    }
    setValue(enteredQuantity.value);
    return enteredQuantity.value!;
  } else {
    return 1;
  }
}

function onMetersBlur() {
  if (!enteredMeters.value || enteredMeters.value < 0) {
    enteredMeters.value = (countInCart.value * productWidthProperty.value) / 1000 || productWidthProperty.value;
  } else {
    enteredMeters.value = parseFloat(enteredMeters.value.toFixed(1));
  }
}

watch(
  [() => enteredMeters.value, () => selectedMeasureType.value],
  ([newEnteredMeters, newSelectedMeasureType]) => {
    if (newEnteredMeters !== undefined && newSelectedMeasureType === "1") {
      enteredMeters.value = parseFloat(newEnteredMeters.toFixed(1));
    }
  },
  { immediate: true, deep: true },
);

watchEffect(() => {
  pendingQuantity.value = null;
});
</script>
